import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@/lib/i18n";
import { useBloquerPartenaire } from "@/pages/admin/RequireAdmin";
import {
  ArrowLeft, Globe, Check, Minus, Plus, Edit2, Trash2,
  Save, X, Loader2, ChevronDown, AlertCircle,
} from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel } from "@/components/admin/AdminPrimitives";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { useVerification } from "@/hooks/useVerifications";
import VerificationService from "@/integrations/service/verification.service";
import {
  useTraductionsVerification,
  useCreateTraductionVerification,
  useUpdateTraductionVerification,
  useDeleteTraductionVerification,
} from "@/hooks/useTraductionVerification";
import { TraductionVerificationDto } from "@/types/verification.types";
import { LANGUES_CONFIG, VERDICT_OPTIONS } from "@/lib/enums";

const LANGUES = LANGUES_CONFIG.map((l) => ({
  code: l.code,
  label: l.label,
  flag: l.flag,
  apiCode: l.apiCode,
}));

type TraductionVerifFormFields = {
  titre: string;
  affirmation: string;
  explicationCourte: string;
  ceQuiCircule: string;
  ceQueNousAvonsVerifie: string;
  preuves: string;
  pourquoiImportant: string;
  partageSecurise: string;
};

const emptyFields = (): TraductionVerifFormFields => ({
  titre: "",
  affirmation: "",
  explicationCourte: "",
  ceQuiCircule: "",
  ceQueNousAvonsVerifie: "",
  preuves: "",
  pourquoiImportant: "",
  partageSecurise: "",
});

// TRAD_FIELDS is defined inside the component to use t()

// ─── Badge verdict ─────────────────────────────────────────────────────────────
function VerdictBadge({ verdict }: { verdict: string }) {
  const v = VERDICT_OPTIONS.find((vd) => vd.value === verdict);
  if (!v) return <span className="text-xs text-muted-foreground font-mono">{verdict}</span>;
  return (
    <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${v.color}`}>
      {v.label}
    </span>
  );
}

// ─── Modal formulaire traduction vérification ─────────────────────────────────
function TraductionVerifModal({
  verificationId,
  langue,
  langLabel,
  initial,
  traductionId,
  frTraduction,
  onClose,
  onSaved,
}: {
  verificationId: number;
  langue: string;
  langLabel: string;
  initial?: TraductionVerifFormFields;
  traductionId?: number;
  frTraduction?: TraductionVerificationDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const createTraduction = useCreateTraductionVerification();
  const updateTraduction = useUpdateTraductionVerification();
  const isEdit = !!traductionId;
  const isFr = langue?.toUpperCase() === "FR";
  const showSplit = !isFr && !!frTraduction;

  const TRAD_FIELDS: { key: keyof TraductionVerifFormFields; label: string; rows: number }[] = [
    { key: "titre", label: "Titre", rows: 1 },
    { key: "affirmation", label: `${t("admin.factcheck.field.affirmation")}`, rows: 2 },
    { key: "explicationCourte", label: t("admin.factcheck.field.explication"), rows: 2 },
    { key: "ceQuiCircule", label: t("admin.factcheck.field.ceQuiCircule"), rows: 3 },
    { key: "ceQueNousAvonsVerifie", label: t("admin.factcheck.field.ceQueNousAvonsVerifie"), rows: 3 },
    { key: "preuves", label: t("admin.factcheck.field.preuves"), rows: 3 },
    { key: "pourquoiImportant", label: t("admin.factcheck.field.pourquoiImportant"), rows: 2 },
    { key: "partageSecurise", label: t("admin.factcheck.field.partageSecurise2"), rows: 2 },
  ];

  const [fields, setFields] = useState<TraductionVerifFormFields>(initial || emptyFields());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (key: keyof TraductionVerifFormFields, val: string) =>
    setFields((p) => ({ ...p, [key]: val }));

  const handleSave = async () => {
    if (!fields.affirmation.trim()) { setError(t("admin.translations.modal.affirmationRequired")); return; }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await updateTraduction.mutateAsync({
          id: traductionId!,
          payload: { langue, ...fields },
        });
      } else {
        await createTraduction.mutateAsync({
          verificationId,
          payload: { langue, ...fields },
        });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || t("admin.common.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`w-full ${showSplit ? "max-w-5xl" : "max-w-2xl"} rounded-lg border border-border bg-background shadow-2xl flex flex-col max-h-[90vh]`}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="font-serif text-base font-semibold text-foreground">
              {isEdit ? t("admin.common.edit") : t("admin.common.add")} {t("admin.common.translations").toLowerCase()} — {langLabel}
            </h2>
            <p className="text-xs text-muted-foreground">
              {isEdit ? t("admin.detail.verif.trad.edit") : t("admin.detail.verif.trad.new")}
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 hover:bg-surface text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-md bg-alert/10 border border-alert/30 px-4 py-2 text-xs text-alert font-medium">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{error}
            </div>
          )}
          {showSplit && (
            <div className="rounded-md border border-border bg-surface/40 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("admin.translations.split.hint")}
            </div>
          )}
          {TRAD_FIELDS.map(({ key, label, rows }) => (
            <div key={key} className={showSplit ? "grid gap-4 md:grid-cols-2" : "space-y-1.5"}>
              {showSplit && (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-muted-foreground">{label} — FR</label>
                  <div className="w-full rounded-md border border-border bg-surface/50 px-3 py-2 text-sm text-foreground/80 min-h-[38px] whitespace-pre-wrap">
                    {frTraduction?.[key] || "—"}
                  </div>
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-muted-foreground">{label}</label>
                <textarea
                  value={fields[key]}
                  onChange={(e) => set(key, e.target.value)}
                  placeholder={label.replace(" *", "")}
                  rows={rows}
                  autoFocus={key === "affirmation"}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground resize-none"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-surface transition-colors">
            {t("admin.common.cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-md bg-foreground px-5 py-2 text-sm font-semibold text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {t("admin.common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Carte langue vérification ────────────────────────────────────────────────
function LangueVerifCard({
  lang,
  trad,
  verificationId,
  frTraduction,
  onRefresh,
}: {
  lang: typeof LANGUES[number];
  trad?: TraductionVerificationDto;
  verificationId: number;
  frTraduction?: TraductionVerificationDto;
  onRefresh: () => void;
}) {
  const { t } = useI18n();
  const deleteTraduction = useDeleteTraductionVerification();
  const [formOpen, setFormOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hasIt = !!trad;

  const handleDelete = () => {
    if (!trad?.id) return;
    setDeleting(true);
    deleteTraduction.mutate(trad.id, {
      onSuccess: () => { setConfirmOpen(false); onRefresh(); },
      onError: () => toast.error(t("admin.common.deleteError")),
      onSettled: () => setDeleting(false),
    });
  };

  return (
    <>
      <div className="rounded-md border border-border bg-background overflow-hidden">
        {/* En-tête carte */}
        <div className="flex items-center gap-3 px-4 py-3">
          <span className="text-xl leading-none">{lang.flag}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">{lang.label}</span>
              {hasIt ? (
                <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-bold bg-verified/10 text-verified">
                  <Check className="h-2.5 w-2.5" /> {t("admin.common.translated")}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-bold bg-surface text-muted-foreground">
                  <Minus className="h-2.5 w-2.5" /> {t("admin.common.missing")}
                </span>
              )}
            </div>
            {trad?.affirmation && (
              <p className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5 italic">« {trad.affirmation} »</p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {hasIt ? (
              <>
                <button
                  onClick={() => setExpanded((p) => !p)}
                  title="Voir le contenu"
                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
                >
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
                </button>
                <button
                  onClick={() => setFormOpen(true)}
                  title="Modifier"
                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-surface transition-colors"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setConfirmOpen(true)}
                  disabled={deleting}
                  title="Supprimer cette traduction"
                  className="rounded p-1 text-muted-foreground hover:text-alert hover:bg-alert/10 transition-colors"
                >
                  {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </button>
              </>
            ) : (
              <button
                onClick={() => setFormOpen(true)}
                className="flex items-center gap-1 rounded-md border border-dashed border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:border-foreground transition-colors"
              >
                <Plus className="h-3 w-3" /> {t("admin.common.add")}
              </button>
            )}
          </div>
        </div>

        {/* Contenu déplié */}
        {expanded && trad && (
          <div className="border-t border-border bg-surface/30 px-4 py-4 space-y-3">
            {trad.titre && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Titre</p>
                <p className="text-sm text-foreground">{trad.titre}</p>
              </div>
            )}
            {trad.affirmation && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.field.affirmation")}</p>
                <p className="text-sm text-foreground italic">« {trad.affirmation} »</p>
              </div>
            )}
            {trad.explicationCourte && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.field.explication")}</p>
                <p className="text-sm text-foreground">{trad.explicationCourte}</p>
              </div>
            )}
            {trad.ceQuiCircule && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.field.ceQuiCircule")}</p>
                <p className="text-sm text-foreground">{trad.ceQuiCircule}</p>
              </div>
            )}
            {trad.ceQueNousAvonsVerifie && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.field.ceQueNousAvonsVerifie")}</p>
                <p className="text-sm text-foreground">{trad.ceQueNousAvonsVerifie}</p>
              </div>
            )}
            {trad.preuves && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.field.preuves")}</p>
                <p className="text-sm text-foreground">{trad.preuves}</p>
              </div>
            )}
            {trad.pourquoiImportant && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.field.pourquoiImportant")}</p>
                <p className="text-sm text-foreground">{trad.pourquoiImportant}</p>
              </div>
            )}
            {trad.partageSecurise && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.field.partageSecurise")}</p>
                <p className="text-sm text-foreground">{trad.partageSecurise}</p>
              </div>
            )}
            {!trad.explicationCourte && !trad.ceQuiCircule && !trad.ceQueNousAvonsVerifie && (
              <p className="text-xs text-muted-foreground italic">{t("admin.detail.verif.onlyAffirmation")}</p>
            )}
          </div>
        )}
      </div>

      {formOpen && (
        <TraductionVerifModal
          verificationId={verificationId}
          langue={lang.apiCode}
          langLabel={lang.label}
          initial={
            trad
              ? {
                  titre: trad.titre || "",
                  affirmation: trad.affirmation || "",
                  explicationCourte: trad.explicationCourte || "",
                  ceQuiCircule: trad.ceQuiCircule || "",
                  ceQueNousAvonsVerifie: trad.ceQueNousAvonsVerifie || "",
                  preuves: trad.preuves || "",
                  pourquoiImportant: trad.pourquoiImportant || "",
                  partageSecurise: trad.partageSecurise || "",
                }
              : undefined
          }
          traductionId={trad?.id}
          frTraduction={frTraduction}
          onClose={() => setFormOpen(false)}
          onSaved={onRefresh}
        />
      )}
      <ConfirmDeleteDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("admin.translations.deleteConfirm.title")}
        description={t("admin.translations.deleteConfirm.desc")}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </>
  );
}

// ─── Page Détail Vérification ─────────────────────────────────────────────────
function DetailVerification() {
  useBloquerPartenaire();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const verificationId = Number(id);
  const queryClient = useQueryClient();

  const { data: verif, isLoading, refetch: refetchVerif } = useVerification(verificationId);
  const { data: traductions, refetch: refetchTraductions } = useTraductionsVerification(verificationId);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [attachmentLabel, setAttachmentLabel] = useState("");
  const [attachmentType, setAttachmentType] = useState("document");
  const [uploadError, setUploadError] = useState("");
  const [deleteAttachmentId, setDeleteAttachmentId] = useState<number | null>(null);

  const uploadAttachment = useMutation({
    mutationFn: ({ file, type, libelle }: { file: File; type: string; libelle?: string }) =>
      VerificationService.uploadAttachment(verificationId, file, { type, libelle }),
    onSuccess: () => {
      setSelectedFile(null);
      setAttachmentLabel("");
      setAttachmentType("document");
      queryClient.invalidateQueries({ queryKey: ["verification", verificationId] });
      refetchVerif();
      refetchTraductions();
    },
    onError: (error: any) => {
      setUploadError(error?.response?.data?.message || error?.message || t("admin.detail.verif.attachments.uploadError"));
    },
  });

  const deleteAttachment = useMutation({
    mutationFn: ({ attachmentId }: { attachmentId: number }) =>
      VerificationService.deleteAttachment(verificationId, attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["verification", verificationId] });
      refetchVerif();
      refetchTraductions();
      setDeleteAttachmentId(null);
    },
  });

  const handleRefresh = () => {
    refetchTraductions();
    refetchVerif();
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadError("");
    uploadAttachment.mutate(
      {
        file: selectedFile,
        type: attachmentType,
        libelle: attachmentLabel || selectedFile.name,
      },
      { onSettled: () => setUploading(false) },
    );
  };

  if (isLoading) {
    return (
      <>
        <AdminTopbar title={t("admin.detail.verif.title")} subtitle="" />
        <div className="flex-1 flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">{t("admin.common.loading")}</span>
        </div>
      </>
    );
  }

  if (!verif) {
    return (
      <>
        <AdminTopbar title={t("admin.detail.verif.notFound")} subtitle="" />
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-muted-foreground">
          <AlertCircle className="h-8 w-8 opacity-40" />
          <p className="text-sm">{t("admin.detail.verif.notExists")}</p>
          <button onClick={() => navigate(-1)} className="text-xs font-semibold text-foreground underline">
            {t("admin.detail.verif.backLink")}
          </button>
        </div>
      </>
    );
  }

  const affirmationDisplay = verif.traductions?.[0]?.affirmation || verif.slug;
  const traductionsList: TraductionVerificationDto[] = traductions || verif.traductions || [];
  const completion = Math.round((traductionsList.length / LANGUES.length) * 100);

  return (
    <>
      <AdminTopbar
        title={`« ${affirmationDisplay} »`}
        subtitle={t("admin.detail.verif.subtitleFmt", { format: verif.format })}
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t("admin.detail.verif.back")}
          </button>
        </div>

        {/* Métadonnées */}
        <ModulePanel title={t("admin.common.metadata")}>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.meta.verdict")}</p>
              <VerdictBadge verdict={verif.verdict} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.meta.format")}</p>
              <span className="font-mono text-sm text-foreground">{verif.format}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.meta.slug")}</p>
              <span className="font-mono text-xs text-muted-foreground">{verif.slug}</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.meta.date")}</p>
              <span className="text-sm text-foreground">
                {verif.date ? new Date(verif.date).toLocaleDateString("fr-FR") : "—"}
              </span>
            </div>
          </div>

          {verif.traductions?.[0]?.explicationCourte && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t("admin.detail.verif.meta.explication")}</p>
              <p className="text-sm text-foreground">{verif.traductions[0].explicationCourte}</p>
            </div>
          )}
        </ModulePanel>

        <ModulePanel title={t("admin.detail.verif.attachments.title")} description={t("admin.detail.verif.attachments.desc")}>
          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.detail.verif.attachments.file")}</label>
                <input
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.common.col.type")}</label>
                <select
                  value={attachmentType}
                  onChange={(e) => setAttachmentType(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="document">Document</option>
                  <option value="image">Image</option>
                  <option value="video">{t("admin.detail.verif.attachments.type.video")}</option>
                  <option value="audio">Audio</option>
                </select>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.detail.verif.attachments.label")}</label>
                <input
                  value={attachmentLabel}
                  onChange={(e) => setAttachmentLabel(e.target.value)}
                  placeholder={t("admin.detail.verif.attachments.labelPlaceholder")}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
            </div>
            {uploadError && <div className="rounded-md border border-alert/30 bg-alert/10 px-3 py-2 text-xs text-alert">{uploadError}</div>}
            <button
              type="button"
              onClick={handleUpload}
              disabled={!selectedFile || uploading || uploadAttachment.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-60"
            >
              {uploading || uploadAttachment.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t("admin.detail.verif.attachments.add")}
            </button>
            {verif.attachments?.length ? (
              <div className="space-y-2">
                {verif.attachments.map((attachment) => (
                  <div key={attachment.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface/50 px-3 py-3">
                    <div>
                      <div className="text-sm font-medium text-foreground">{attachment.libelle || attachment.type}</div>
                      <a href={attachment.url} target="_blank" rel="noreferrer" className="text-xs text-verified underline">{t("admin.detail.verif.attachments.viewFile")}</a>
                    </div>
                    <button
                      type="button"
                      onClick={() => attachment.id && setDeleteAttachmentId(attachment.id)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-alert"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> {t("admin.common.delete")}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-border px-3 py-4 text-sm text-muted-foreground">{t("admin.detail.verif.attachments.empty")}</div>
            )}
          </div>
        </ModulePanel>

        {/* Traductions */}
        <ModulePanel
          title={t("admin.common.translations")}
          description={t("admin.common.langues", { count: traductionsList.length, total: LANGUES.length, pct: completion })}
        >
          <div className="mb-5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Globe className="h-3 w-3" /> {t("admin.common.multilingual")}
              </span>
              <span className="text-[10px] font-bold text-muted-foreground">{completion}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-surface overflow-hidden">
              <div
                className="h-full rounded-full bg-verified transition-all duration-500"
                style={{ width: `${completion}%` }}
              />
            </div>
          </div>

          {/* Une carte par langue */}
          <div className="space-y-2">
            {(() => {
              const frTrad = traductionsList.find((t) => t.langue?.toLowerCase() === "fr");
              return LANGUES.map((lang) => {
                const trad = traductionsList.find(
                  (t) => t.langue?.toLowerCase() === lang.code.toLowerCase()
                );
                return (
                  <LangueVerifCard
                    key={lang.code}
                    lang={lang}
                    trad={trad}
                    verificationId={verificationId}
                    frTraduction={frTrad}
                    onRefresh={handleRefresh}
                  />
                );
              });
            })()}
          </div>
        </ModulePanel>
      </div>

      <ConfirmDeleteDialog
        open={!!deleteAttachmentId}
        onOpenChange={(open) => !open && setDeleteAttachmentId(null)}
        title={t("admin.detail.verif.attachments.deleteConfirm.title")}
        description={t("admin.detail.verif.attachments.deleteConfirm.desc")}
        onConfirm={() => deleteAttachmentId && deleteAttachment.mutate({ attachmentId: deleteAttachmentId })}
        loading={deleteAttachment.isPending}
      />
    </>
  );
}

export default DetailVerification;
